import { parseObj, type ObjMesh } from "./objImport";

/**
 * Mesh → solid voxel grid.
 *
 * Voxelization by ray parity: for every voxel row, cast a ray along +x
 * (Möller–Trumbore against every triangle) and fill the spans between
 * even/odd crossing pairs — the classic point-in-mesh test, exact for
 * watertight meshes regardless of orientation. Open meshes fill what their
 * geometry actually encloses.
 *
 * Orientation: OBJ models are usually y-up, LEGO builds grow in build-z.
 * Mapping: obj.x → build x (studs right), obj.z → build y (studs deep),
 * obj.y → build z (layers up).
 */

export const MAX_GRID = 24; // studs / layers on the longest side

export interface VoxelGrid {
  /** grid[z * sizeY * sizeX + y * sizeX + x] = 1 when solid */
  data: Uint8Array;
  sizeX: number;
  sizeY: number;
  sizeZ: number;
}

/** Map OBJ space to build space and scale so the longest side = target.
 *
 *  A uniform cell size (longest extent / target) is used for every axis and
 *  the mesh is remapped into cell units, so proportions are exact — no
 *  per-axis rounding skew, and no part of the model is cut by grid rounding.
 */
export const fitMeshToGrid = (
  mesh: ObjMesh,
  target: number = MAX_GRID,
): { mesh: ObjMesh; sizeX: number; sizeY: number; sizeZ: number } => {
  const { min, max } = mesh.bounds;
  const sizeObj = [max[0] - min[0], max[1] - min[1], max[2] - min[2]];
  const longest = Math.max(...sizeObj, Number.EPSILON);
  const cell = longest / target; // world units per voxel

  // At least one cell per axis, so flat models still occupy one layer
  const sizeX = Math.max(1, Math.ceil(sizeObj[0] / cell) || 1);
  const sizeY = Math.max(1, Math.ceil(sizeObj[2] / cell) || 1);
  const sizeZ = Math.max(1, Math.ceil(sizeObj[1] / cell) || 1);

  const remap = ([x, y, z]: [number, number, number]): [number, number, number] => [
    (x - min[0]) / cell,
    (z - min[2]) / cell, // build plan depth ← obj z
    (y - min[1]) / cell, // build height ← obj y
  ];

  return {
    mesh: {
      vertices: mesh.vertices.map(remap),
      triangles: mesh.triangles,
      bounds: {
        min: [0, 0, 0],
        max: [sizeObj[0] / cell, sizeObj[2] / cell, sizeObj[1] / cell],
      },
    },
    sizeX,
    sizeY,
    sizeZ,
  };
};

/** Ray direction (1, 0, 0): only y/z of the origin matter per row. */
const rayParitySpans = (
  vertices: Array<[number, number, number]>,
  triangles: Array<[number, number, number]>,
  originY: number,
  originZ: number,
  sizeX: number,
): Array<[number, number]> => {
  const crossings: number[] = [];

  for (const [ia, ib, ic] of triangles) {
    const p0 = vertices[ia];
    const p1 = vertices[ib];
    const p2 = vertices[ic];

    const e1x = p1[0] - p0[0];
    const e1y = p1[1] - p0[1];
    const e1z = p1[2] - p0[2];
    const e2x = p2[0] - p0[0];
    const e2y = p2[1] - p0[1];
    const e2z = p2[2] - p0[2];

    // h = dir × e2 with dir = (1,0,0) → (0, -e2z, e2y)
    const hy = -e2z;
    const hz = e2y;
    const a = e1y * hy + e1z * hz; // dot(e1, h); e1.x * 0 vanishes
    if (Math.abs(a) < 1e-12) continue; // ray parallel to triangle plane

    const sy = originY - p0[1];
    const sz = originZ - p0[2];
    const sx = -p0[0]; // ray origin x = 0
    const f = 1 / a;
    const u = f * (sy * hy + sz * hz);
    if (u < 0 || u > 1) continue;

    // q = s × e1
    const qx = sy * e1z - sz * e1y;
    const qy = sz * e1x - sx * e1z;
    const qz = sx * e1y - sy * e1x;
    const v = f * qx; // dot(q, dir) = qx (dir = +x)
    if (v < 0 || u + v > 1) continue;

    const t = f * (e2x * qx + e2y * qy + e2z * qz);
    if (t < 0) continue; // crossing behind the ray origin

    crossings.push(t);
  }

  crossings.sort((m, n) => m - n);
  const spans: Array<[number, number]> = [];
  for (let i = 0; i + 1 < crossings.length; i += 2) {
    const x0 = Math.max(0, Math.ceil(crossings[i]));
    const x1 = Math.min(sizeX - 1, Math.floor(crossings[i + 1]));
    if (x1 >= x0) spans.push([x0, x1]);
  }
  return spans;
};

/**
 * Voxelize a watertight mesh into a solid grid: per voxel row, +x ray
 * parity against every triangle; spans between even/odd crossings are solid.
 */
export const voxelizeMesh = (
  mesh: ObjMesh,
  target: number = MAX_GRID,
): VoxelGrid => {
  const fitted = fitMeshToGrid(mesh, target);
  const { sizeX, sizeY, sizeZ } = fitted;
  const { vertices, triangles } = fitted.mesh;

  const data = new Uint8Array(sizeX * sizeY * sizeZ);

  for (let z = 0; z < sizeZ; z++) {
    // Small irrationals dodge exact edge/vertex hits
    const originZ = z + 0.5 + 2e-4;
    for (let y = 0; y < sizeY; y++) {
      const originY = y + 0.5 + 1e-4;
      const spans = rayParitySpans(vertices, triangles, originY, originZ, sizeX);
      for (const [x0, x1] of spans) {
        for (let x = x0; x <= x1; x++) {
          data[z * sizeY * sizeX + y * sizeX + x] = 1;
        }
      }
    }
  }

  return { data, sizeX, sizeY, sizeZ };
};

/** Convenience: OBJ text → mesh (null when the file has no usable geometry). */
export const parseObjSafe = (source: string): ObjMesh | null => {
  try {
    return parseObj(source);
  } catch {
    return null;
  }
};
